/**
 * DraftContext — manages draft state globally.
 *
 * - draftedItemIds: Set<string> used by feed cards to show presence indicator
 * - drafts: Draft[] for the currently open item (loaded on demand)
 * - saveDraft(): dedup-aware save, triggers on copy/share/explicit save
 */
import {
  createContext, useContext, useState, useCallback,
  useRef, useEffect, type ReactNode,
} from 'react'
import type { Draft, DraftSettings } from '../services/draftService'
import {
  createDraft, listDraftsForItem, deleteDraft,
} from '../services/draftService'

interface DraftContextValue {
  // Feed badge data
  draftedItemIds: Set<string>
  // Per-item drafts (loaded when editor opens)
  drafts: Draft[]
  activeDraftIndex: number          // index into drafts[] (0 = newest)
  activeDraft: Draft | null
  isDraftLoading: boolean
  // Actions
  loadDraftsForItem: (item_id: string) => Promise<void>
  selectDraft: (index: number) => void
  saveDraft: (params: {
    item_id: string
    item_url: string
    item_title: string
    post_text: string
    settings: DraftSettings
  }) => Promise<void>
  removeDraft: (draft_id: string) => Promise<void>
  clearItemDrafts: () => void
}

const DraftContext = createContext<DraftContextValue | undefined>(undefined)

export function DraftProvider({ children }: { children: ReactNode }) {

  const [draftedItemIds, setDraftedItemIds] = useState<Set<string>>(new Set())
  const [drafts, setDrafts] = useState<Draft[]>([])
  const [activeDraftIndex, setActiveDraftIndex] = useState(0)
  const [isDraftLoading, setIsDraftLoading] = useState(false)

  // Ref to last saved text for dedup (client-side fast path)
  const lastSavedTextRef = useRef<string>('')

  // Drafted items tracking disabled for public site

  // No drafts fetch needed for public site
  useEffect(() => {}, [])

  const loadDraftsForItem = useCallback(async (item_id: string) => {
    setIsDraftLoading(true)
    try {
      const loaded = await listDraftsForItem(item_id)
      setDrafts(loaded)
      setActiveDraftIndex(0)
      if (loaded.length > 0) {
        lastSavedTextRef.current = loaded[0].post_text
      }
    } catch (e) {
      console.warn('Failed to load drafts for item', e)
      setDrafts([])
    } finally {
      setIsDraftLoading(false)
    }
  }, [])

  const selectDraft = useCallback((index: number) => {
    setActiveDraftIndex(index)
  }, [])

  const saveDraft = useCallback(async (params: {
    item_id: string
    item_url: string
    item_title: string
    post_text: string
    settings: DraftSettings
  }) => {
    if (!params.post_text.trim()) return
    // Client-side dedup fast path
    if (params.post_text === lastSavedTextRef.current) return

    try {
      const saved = await createDraft(params)
      lastSavedTextRef.current = saved.post_text

      // Prepend to local drafts list, re-apply cap
      setDrafts(prev => {
        const updated = [saved, ...prev.filter(d => d.draft_id !== saved.draft_id)]
        return updated.slice(0, 10)
      })
      setActiveDraftIndex(0)

      // Ensure item appears in badge set
      setDraftedItemIds(prev => new Set([...prev, params.item_id]))
    } catch (e) {
      console.error('Failed to save draft', e)
    }
  }, [])

  const removeDraft = useCallback(async (draft_id: string) => {
    try {
      await deleteDraft(draft_id)
      setDrafts(prev => {
        const updated = prev.filter(d => d.draft_id !== draft_id)
        return updated
      })
      setActiveDraftIndex(0)
    } catch (e) {
      console.error('Failed to delete draft', e)
    }
  }, [])

  const clearItemDrafts = useCallback(() => {
    setDrafts([])
    setActiveDraftIndex(0)
    lastSavedTextRef.current = ''
  }, [])

  const activeDraft = drafts[activeDraftIndex] ?? null

  return (
    <DraftContext.Provider value={{
      draftedItemIds,
      drafts,
      activeDraftIndex,
      activeDraft,
      isDraftLoading,
      loadDraftsForItem,
      selectDraft,
      saveDraft,
      removeDraft,
      clearItemDrafts,
    }}>
      {children}
    </DraftContext.Provider>
  )
}

export function useDraft() {
  const ctx = useContext(DraftContext)
  if (!ctx) throw new Error('useDraft must be used within DraftProvider')
  return ctx
}
